import React, { useMemo, useRef, useState } from 'react';
import {
  Box, Stack, Typography, Button, Paper, alpha, useTheme,
  Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Alert, LinearProgress, Divider, Tabs, Tab,
  Tooltip,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import {
  CloudUpload, Description, CheckCircle, WarningAmber,
  ErrorOutline, Refresh, Download as DownloadIcon,
  Block as BlockIcon, InsertDriveFile, Add,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';

import feeReconciliationService from '../../services/feeReconciliationService';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { useAuth } from '../../context/AuthContext';
import {
  BankRow, ReconciliationResult, ReconciliationRowResult,
} from '../../types/fees';
import {
  parseReconciliationCsv, buildReconciliationTemplateCsv,
  buildExceptionsCsv, downloadCsv, EXCEPTION_LABEL,
} from '../../utils/reconciliationCsv';
import ChallanNumberCell from '../../components/fees/ChallanNumberCell';
import ManualReconciliationDialog from '../../components/fees/ManualReconciliationDialog';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';

/**
 * FeeReconciliationPage — batch settlement flow.
 *
 * Journey: upload CSV → parse client-side → preview against backend → review
 * matched + exceptions → commit → download exceptions for follow-up.
 *
 * State machine:
 *   'idle'      → nothing loaded yet
 *   'parsed'    → CSV parsed, ready to preview
 *   'previewed' → backend has classified rows, ready to commit
 *   'committed' → payments recorded, exceptions ready to export
 *
 * Every transition is initiated by the bursar so we never silently commit
 * money movements.
 */

type Phase = 'idle' | 'parsed' | 'previewed' | 'committed';
type ViewFilter = 'all' | 'matched' | 'exception';

const FeeReconciliationPage: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { canRead, canWrite } = useModulePermissions();
  const { currency } = useAuth();

  const canReadFees = canRead('fees');
  const canWriteFees = canWrite('fees');

  const [phase, setPhase] = useState<Phase>('idle');
  const [fileName, setFileName] = useState<string | null>(null);
  const [rows, setRows] = useState<BankRow[]>([]);
  const [result, setResult] = useState<ReconciliationResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState<ViewFilter>('all');
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [manualOpen, setManualOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const currencyFmt = useCurrencyFormat();

  // ─── File handling ──────────────────────────────────────────────────────

  const resetAll = () => {
    setPhase('idle');
    setFileName(null);
    setRows([]);
    setResult(null);
    setBusy(false);
    setFilter('all');
    setParseErrors([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFile = async (file: File) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      enqueueSnackbar('Only .csv files are supported. Download the template if you need the expected shape.', { variant: 'warning' });
      return;
    }
    try {
      const text = await file.text();
      const parsed = parseReconciliationCsv(text);
      if (parsed.errors.length > 0) {
        setParseErrors(parsed.errors.map(e => e.message));
        setRows([]);
        setFileName(file.name);
        setPhase('idle');
        return;
      }
      if (parsed.rows.length === 0) {
        setParseErrors(['File contains no data rows.']);
        setRows([]);
        setFileName(file.name);
        setPhase('idle');
        return;
      }
      setParseErrors([]);
      setRows(parsed.rows);
      setFileName(file.name);
      setResult(null);
      setPhase('parsed');
      enqueueSnackbar(`Parsed ${parsed.rows.length} row${parsed.rows.length === 1 ? '' : 's'} from ${file.name}`, { variant: 'info' });
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Failed to read the file', { variant: 'error' });
    }
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) void handleFile(f);
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) void handleFile(f);
  };

  // ─── Preview / Commit ───────────────────────────────────────────────────

  const runPreview = async () => {
    if (rows.length === 0) return;
    setBusy(true);
    try {
      const res = await feeReconciliationService.previewReconciliation(rows);
      setResult(res);
      setPhase('previewed');
      setFilter(res.exceptions > 0 ? 'exception' : 'all');
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Failed to preview reconciliation', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const runCommit = async () => {
    if (rows.length === 0) return;
    setBusy(true);
    try {
      const res = await feeReconciliationService.commitReconciliation(rows);
      setResult(res);
      setPhase('committed');
      setFilter(res.exceptions > 0 ? 'exception' : 'matched');
      enqueueSnackbar(
        `Reconciled ${res.matched} of ${res.totalRows} row${res.totalRows === 1 ? '' : 's'}`,
        { variant: 'success' },
      );
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Failed to commit reconciliation', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const onDownloadTemplate = () => {
    downloadCsv('reconciliation-template.csv', buildReconciliationTemplateCsv());
  };

  const onDownloadExceptions = () => {
    if (!result) return;
    const stamp = format(new Date(), 'yyyy-MM-dd');
    downloadCsv(`reconciliation-exceptions-${stamp}.csv`, buildExceptionsCsv(result.rows));
  };

  // ─── Derived ────────────────────────────────────────────────────────────

  const filteredRows = useMemo(() => {
    if (!result) return [];
    if (filter === 'all') return result.rows;
    return result.rows.filter(r => r.status === filter);
  }, [result, filter]);

  const exceptionsCsvReady = result && result.exceptions > 0;

  // ─── Guards ─────────────────────────────────────────────────────────────

  if (!canReadFees) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning" icon={<BlockIcon />}>You don't have permission to view fee reconciliation.</Alert>
      </Box>
    );
  }

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* Header */}
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'flex-start' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>Bank Reconciliation</Typography>
          <Typography variant="body2" color="text.secondary">
            Upload a bank settlement file and auto-record payments for every row that cleanly matches an outstanding challan. Ambiguous rows are surfaced as exceptions for you to resolve.
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          {phase !== 'idle' && (
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={resetAll}
              disabled={busy}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Start over
            </Button>
          )}
          {canWriteFees && (
            <Button
              variant="contained"
              disableElevation
              startIcon={<Add />}
              onClick={() => setManualOpen(true)}
              disabled={busy}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Record payment
            </Button>
          )}
        </Box>
      </Stack>

      {/* Upload card — shown when no file loaded */}
      {phase === 'idle' && (
        <Paper
          variant="outlined"
          onDragOver={e => e.preventDefault()}
          onDrop={onDrop}
          sx={{
            p: 4, borderRadius: 2,
            borderStyle: 'dashed',
            borderColor: theme.palette.divider,
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'background 120ms',
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) },
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={onFileChange}
          />
          <CloudUpload sx={{ fontSize: 44, color: theme.palette.primary.main, mb: 1 }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
            Drop a settlement CSV here, or click to browse
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Expected columns: challanNumber, amount, txnDate, bankRef · Up to 1,000 rows per file
          </Typography>
          <Button
            variant="text"
            startIcon={<Description />}
            onClick={(e) => { e.stopPropagation(); onDownloadTemplate(); }}
            size="small"
          >
            Download CSV template
          </Button>

          {parseErrors.length > 0 && (
            <Alert severity="error" variant="outlined" sx={{ mt: 3, textAlign: 'left' }}>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                Couldn't parse {fileName ?? 'the file'}
              </Typography>
              {parseErrors.map((msg, i) => (
                <Typography key={i} variant="caption" component="div" color="text.secondary">
                  {msg}
                </Typography>
              ))}
            </Alert>
          )}
        </Paper>
      )}

      {/* Parsed — show file summary + preview action */}
      {phase === 'parsed' && (
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1 }}>
              <Box sx={{
                width: 40, height: 40, borderRadius: 1.5,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main,
              }}>
                <InsertDriveFile />
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={700}>{fileName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {rows.length} row{rows.length === 1 ? '' : 's'} parsed · ready to preview
                </Typography>
              </Box>
            </Stack>
            <Button
              variant="contained"
              disableElevation
              onClick={runPreview}
              disabled={busy}
              sx={{ borderRadius: 2, minWidth: 200 }}
            >
              {busy ? 'Checking…' : 'Preview reconciliation'}
            </Button>
          </Stack>
          {busy && <LinearProgress sx={{ mt: 2 }} />}
        </Paper>
      )}

      {/* Previewed / Committed — summary + table */}
      {result && (phase === 'previewed' || phase === 'committed') && (
        <Stack spacing={2}>
          <SummaryStrip
            result={result}
            phase={phase}
            currency={currency}
            currencyFmt={currencyFmt}
            theme={theme}
          />

          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={1}
              sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${theme.palette.divider}`, bgcolor: alpha(theme.palette.text.primary, 0.02) }}
            >
              <Tabs
                value={filter}
                onChange={(_, v) => setFilter(v as ViewFilter)}
                sx={{ minHeight: 36, '& .MuiTab-root': { textTransform: 'none', minHeight: 36, py: 0.5 } }}
              >
                <Tab value="all"       label={`All (${result.totalRows})`} />
                <Tab value="matched"   label={`Matched (${result.matched})`} />
                <Tab value="exception" label={`Exceptions (${result.exceptions})`} />
              </Tabs>
              <Box sx={{ flex: 1 }} />
              {exceptionsCsvReady && (
                <Tooltip title="Download exceptions to chase offline in Excel">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadIcon fontSize="small" />}
                    onClick={onDownloadExceptions}
                    sx={{ borderRadius: 1.5 }}
                  >
                    Download exceptions
                  </Button>
                </Tooltip>
              )}
            </Stack>

            <ResultsTable
              rows={filteredRows}
              phase={phase}
              currencyFmt={currencyFmt}
              theme={theme}
            />
          </Paper>

          {/* Commit action bar — visible only when previewed */}
          {phase === 'previewed' && (
            <Paper
              variant="outlined"
              sx={{
                p: 2, borderRadius: 2,
                bgcolor: alpha(theme.palette.success.main, 0.03),
                borderColor: alpha(theme.palette.success.main, 0.24),
              }}
            >
              <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'stretch', md: 'center' }} spacing={2}>
                <Box sx={{ flex: 1 }}>
                  {result.matched > 0 ? (
                    <>
                      <Typography variant="body2" fontWeight={700}>
                        Ready to record {result.matched} payment{result.matched === 1 ? '' : 's'} totalling {currencyFmt(result.matchedAmount)}.
                      </Typography>
                      {result.exceptions > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          {result.exceptions} exception{result.exceptions === 1 ? '' : 's'} will be skipped. Resolve them manually after this run, or download the CSV above.
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography variant="body2" fontWeight={700}>
                      No rows can be auto-recorded — every row is an exception. Resolve them before running again.
                    </Typography>
                  )}
                </Box>
                <Button
                  variant="contained"
                  color="success"
                  disableElevation
                  onClick={runCommit}
                  disabled={busy || result.matched === 0 || !canWriteFees}
                  sx={{ borderRadius: 2, minWidth: 240, fontWeight: 700 }}
                >
                  {busy
                    ? 'Recording…'
                    : result.matched > 0
                      ? `Reconcile ${result.matched} row${result.matched === 1 ? '' : 's'}`
                      : 'Nothing to reconcile'}
                </Button>
              </Stack>
              {busy && <LinearProgress color="success" sx={{ mt: 2 }} />}
            </Paper>
          )}

          {/* Committed banner */}
          {phase === 'committed' && (
            <Alert
              severity={result.exceptions > 0 ? 'warning' : 'success'}
              variant="outlined"
              icon={result.exceptions > 0 ? <WarningAmber /> : <CheckCircle />}
            >
              <Typography variant="body2" fontWeight={700}>
                Reconciliation complete · {result.matched} payment{result.matched === 1 ? '' : 's'} recorded ({currencyFmt(result.matchedAmount)}).
              </Typography>
              {result.exceptions > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {result.exceptions} row{result.exceptions === 1 ? '' : 's'} skipped. Download the exceptions CSV above and resolve them individually.
                </Typography>
              )}
            </Alert>
          )}
        </Stack>
      )}

      <ManualReconciliationDialog
        open={manualOpen}
        onClose={() => setManualOpen(false)}
      />
    </Box>
  );
};

// ─── Subcomponents kept inline while the page is small ─────────────────────

interface SummaryStripProps {
  result: ReconciliationResult;
  phase: Phase;
  currency: string;
  currencyFmt: (n: number) => string;
  theme: Theme;
}

const SummaryStrip: React.FC<SummaryStripProps> = React.memo(({ result, phase, currencyFmt, theme }) => (
  <Paper
    variant="outlined"
    sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.text.primary, 0.015) }}
  >
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      divider={<Divider orientation="vertical" flexItem />}
      spacing={0}
    >
      <StripCell label="Rows" value={String(result.totalRows)} theme={theme} />
      <StripCell
        label={phase === 'committed' ? 'Recorded' : 'Will match'}
        value={String(result.matched)}
        sub={currencyFmt(result.matchedAmount)}
        tint="success"
        theme={theme}
      />
      <StripCell
        label="Exceptions"
        value={String(result.exceptions)}
        tint={result.exceptions > 0 ? 'warning' : 'neutral'}
        theme={theme}
      />
      <StripCell
        label="Total in file"
        value={currencyFmt(result.totalAmount)}
        theme={theme}
      />
    </Stack>
  </Paper>
));

interface StripCellProps {
  label: string;
  value: string;
  sub?: string;
  tint?: 'neutral' | 'success' | 'warning';
  theme: Theme;
}

const StripCell: React.FC<StripCellProps> = ({ label, value, sub, tint = 'neutral', theme }) => {
  const color =
    tint === 'success' ? theme.palette.success.dark
    : tint === 'warning' ? theme.palette.warning.dark
    : theme.palette.text.primary;
  return (
    <Box sx={{ flex: 1, px: { xs: 0, sm: 2 }, py: { xs: 1, sm: 0 } }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ letterSpacing: 0.4, textTransform: 'uppercase' }}>
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={700} sx={{ color, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      )}
    </Box>
  );
};

interface ResultsTableProps {
  rows: ReconciliationRowResult[];
  phase: Phase;
  currencyFmt: (n: number) => string;
  theme: Theme;
}

const ResultsTable: React.FC<ResultsTableProps> = React.memo(({ rows, phase, currencyFmt, theme }) => {
  if (rows.length === 0) {
    return (
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No rows in this filter.
        </Typography>
      </Box>
    );
  }
  return (
    <Table size="small">
      <TableHead>
        <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.02) }}>
          <TableCell sx={{ pl: 2, width: 44 }}>#</TableCell>
          <TableCell>Challan</TableCell>
          <TableCell>Student</TableCell>
          <TableCell align="right">Amount</TableCell>
          <TableCell align="right">Outstanding</TableCell>
          <TableCell>Status</TableCell>
          {phase === 'committed' && <TableCell>Receipt</TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map(r => (
          <ResultsRow key={r.index} row={r} phase={phase} currencyFmt={currencyFmt} theme={theme} />
        ))}
      </TableBody>
    </Table>
  );
});

interface ResultsRowProps {
  row: ReconciliationRowResult;
  phase: Phase;
  currencyFmt: (n: number) => string;
  theme: Theme;
}

const ResultsRow: React.FC<ResultsRowProps> = React.memo(({ row, phase, currencyFmt, theme }) => {
  const isMatched = row.status === 'matched';
  const label = row.exceptionCode ? EXCEPTION_LABEL[row.exceptionCode] : 'Matched';
  const isCommitted = phase === 'committed' && isMatched;
  const statusColor = isMatched ? theme.palette.success.main : theme.palette.warning.main;

  return (
    <TableRow hover>
      <TableCell sx={{ pl: 2, color: 'text.secondary' }}>{row.index + 1}</TableCell>
      <TableCell>
        <ChallanNumberCell value={row.challanNumber} />
      </TableCell>
      <TableCell>
        <Typography variant="body2">{row.studentName ?? '—'}</Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
          {currencyFmt(row.amount)}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Typography
          variant="body2"
          sx={{
            fontVariantNumeric: 'tabular-nums',
            color: row.outstanding == null ? 'text.disabled' : 'text.primary',
          }}
        >
          {row.outstanding != null ? currencyFmt(row.outstanding) : '—'}
        </Typography>
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
          <Tooltip title={row.exceptionMessage ?? ''} disableHoverListener={isMatched}>
            <Chip
              size="small"
              icon={isMatched
                ? <CheckCircle sx={{ fontSize: '0.85rem !important' }} />
                : <ErrorOutline sx={{ fontSize: '0.85rem !important' }} />}
              label={isCommitted ? 'Recorded' : label}
              variant="outlined"
              sx={{
                height: 22, fontSize: '0.7rem', fontWeight: 600,
                borderColor: alpha(statusColor, 0.4),
                color: isMatched ? theme.palette.success.dark : theme.palette.warning.dark,
                bgcolor: alpha(statusColor, 0.08),
                '& .MuiChip-icon': { color: 'inherit' },
              }}
            />
          </Tooltip>
          {isMatched && row.arrearsCount != null && row.arrearsCount > 0 && (
            <Tooltip
              title={`Payment split: ${currencyFmt(row.arrearsAmount ?? 0)} to ${row.arrearsCount} prior challan${row.arrearsCount === 1 ? '' : 's'}, remainder to this challan.`}
              arrow
              placement="top"
            >
              <Chip
                size="small"
                label={`+${row.arrearsCount} arrears`}
                variant="outlined"
                sx={{
                  height: 20, fontSize: '0.65rem', fontWeight: 700, letterSpacing: 0.2,
                  borderColor: alpha(theme.palette.warning.main, 0.4),
                  color: theme.palette.warning.dark,
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            </Tooltip>
          )}
        </Stack>
      </TableCell>
      {phase === 'committed' && (
        <TableCell>
          {row.receiptNumber ? (
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
              {row.receiptNumber}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.disabled">—</Typography>
          )}
        </TableCell>
      )}
    </TableRow>
  );
});

export default FeeReconciliationPage;
